import React, { useState, useEffect } from 'react';
import {
  Card,
  Title,
  Text,
  Button,
  Flex,
  Divider,
  Callout,
  Badge,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Icon
} from "@tremor/react";
import Nav from "../../components/nav";
import { 
  PlusIcon, 
  GlobeAltIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  PlayIcon,
  TrashIcon,
  PencilIcon,
  LockClosedIcon
} from "@heroicons/react/24/outline";
import * as webhookAPI from "../../services/webhook";
import WebhookForm from "./webhook-form";
import { toFriendlyTime } from "../../services/utils";
import { useAuth } from "../../services/AuthProvider";
import { getSubscription } from "../../services/user";

const EmptyIntegrations = ({ onAdd }) => {
  return (
    <div className="mt-4 flex h-52 items-center justify-center rounded-tremor-small border border-dashed border-tremor-border p-4">
      <div className="text-center">
        <GlobeAltIcon
          className="mx-auto h-7 w-7 text-tremor-content-subtle"
          aria-hidden={true}
        />
        <p className="mt-2 text-tremor-default font-medium text-tremor-content-strong">
          No Integrations Configured
        </p>
        <p className="text-tremor-default text-tremor-content">
          Connect external services to receive real-time data and alerts
        </p>
        <Button
          icon={PlusIcon}
          onClick={onAdd}
          type="button"
          className="mt-6"
        >
          Add Integration
        </Button>
      </div>
    </div>
  );
};

const WebhookTable = ({ webhooks, onEdit, onDelete, onTest }) => {
  const [testing, setTesting] = useState({});

  const handleTest = async (webhookId) => {
    setTesting(prev => ({ ...prev, [webhookId]: true }));
    try {
      await onTest(webhookId);
    } finally {
      setTesting(prev => ({ ...prev, [webhookId]: false }));
    }
  };

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Name</TableHeaderCell>
          <TableHeaderCell>URL</TableHeaderCell>
          <TableHeaderCell>Events</TableHeaderCell>
          <TableHeaderCell>Status</TableHeaderCell>
          <TableHeaderCell>Last Delivery</TableHeaderCell>
          <TableHeaderCell>Actions</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {webhooks.map((webhook) => (
          <TableRow key={webhook._id}>
            <TableCell>
              <div>
                <Text className="font-medium">{webhook.name}</Text>
                {webhook.secret && (
                  <Badge size="xs" color="blue" className="mt-1">
                    Secured
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Text className="font-mono text-xs truncate max-w-xs">
                {webhook.url}
              </Text>
            </TableCell>
            <TableCell>
              <div className="flex gap-1">
                {webhook.eventTypes.map(type => (
                  <Badge key={type} size="xs" variant="neutral">
                    {type.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <Icon
                  icon={webhook.enabled ? CheckCircleIcon : XCircleIcon}
                  color={webhook.enabled ? "green" : "red"}
                  size="sm"
                />
                <Text>{webhook.enabled ? "Enabled" : "Disabled"}</Text>
              </div>
            </TableCell>
            <TableCell>
              <div>
                {webhook.stats?.lastDelivery ? (
                  <>
                    <Text className="text-xs">
                      {toFriendlyTime(webhook.stats.lastDelivery)}
                    </Text>
                    <div className="flex gap-1 mt-1">
                      <Badge size="xs" color="green">
                        {webhook.stats.successfulDeliveries || 0} success
                      </Badge>
                      {webhook.stats.failedDeliveries > 0 && (
                        <Badge size="xs" color="red">
                          {webhook.stats.failedDeliveries} failed
                        </Badge>
                      )}
                    </div>
                  </>
                ) : (
                  <Text className="text-xs text-tremor-content-subtle">
                    No deliveries yet
                  </Text>
                )}
              </div>
            </TableCell>
            <TableCell>
              <div className="flex gap-1">
                <Button
                  size="xs"
                  variant="secondary"
                  icon={PlayIcon}
                  onClick={() => handleTest(webhook._id)}
                  loading={testing[webhook._id]}
                >
                  Test
                </Button>
                <Button
                  size="xs"
                  variant="secondary"
                  icon={PencilIcon}
                  onClick={() => onEdit(webhook)}
                >
                  Edit
                </Button>
                <Button
                  size="xs"
                  variant="secondary"
                  icon={TrashIcon}
                  color="red"
                  onClick={() => onDelete(webhook._id)}
                >
                  Delete
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default function Integrations() {
  const { currentUser } = useAuth();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Check if user is premium (light or plus tier)
  const isPremium = subscription?.tier === 'light' || subscription?.tier === 'plus';
  

  useEffect(() => {
    const loadSubscriptionAndWebhooks = async () => {
      try {
        // First load subscription to check premium status
        const subData = await getSubscription();
        setSubscription(subData);
        
        // If user is premium, load webhooks
        if (subData?.tier === 'light' || subData?.tier === 'plus') {
          const webhooksData = await webhookAPI.fetchWebhooks();
          setWebhooks(webhooksData);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load integrations data');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadSubscriptionAndWebhooks();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchWebhooks = async () => {
    try {
      const data = await webhookAPI.fetchWebhooks();
      setWebhooks(data);
    } catch (err) {
      setError('Failed to load integrations');
      console.error('Error fetching webhooks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWebhook = () => {
    setEditingWebhook(null);
    setShowForm(true);
  };

  const handleEditWebhook = (webhook) => {
    setEditingWebhook(webhook);
    setShowForm(true);
  };

  const handleFormSubmit = async (webhookData) => {
    try {
      if (editingWebhook) {
        await webhookAPI.updateWebhook(editingWebhook._id, webhookData);
        setSuccess('Integration updated successfully');
      } else {
        await webhookAPI.createWebhook(webhookData);
        setSuccess('Integration created successfully');
      }
      
      setShowForm(false);
      setEditingWebhook(null);
      await fetchWebhooks();
    } catch (err) {
      setError(err.message || 'Failed to save integration');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) {
      return;
    }

    try {
      await webhookAPI.deleteWebhook(webhookId);
      setSuccess('Integration deleted successfully');
      await fetchWebhooks();
    } catch (err) {
      setError('Failed to delete integration');
    }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      await webhookAPI.testWebhook(webhookId);
      setSuccess('Test webhook sent successfully');
    } catch (err) {
      setError('Failed to send test webhook: ' + (err.message || 'Unknown error'));
    }
  };

  // Clear messages after 3 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  if (loading) {
    return (
      <div>
        <Nav />
        <main className="p-4 md:p-10 mx-auto max-w-7xl">
          <Title>Integrations</Title>
          <Text>Loading...</Text>
        </main>
      </div>
    );
  }

  // Show premium-only message for non-premium users
  if (!isPremium) {
    return (
      <div>
        <Nav />
        <main className="p-4 md:p-10 mx-auto max-w-7xl">
          <Title>Integrations</Title>
          <Text>Connect external services to receive real-time data and alerts from your devices.</Text>
          
          <Divider />
          
          <Card className="mt-6">
            <div className="flex flex-col items-center justify-center py-12">
              <LockClosedIcon className="h-12 w-12 text-tremor-content-subtle mb-4" />
              <Title>Premium Feature</Title>
              <Text className="text-center mt-2 mb-6 max-w-md">
                Webhook integrations are available for Light and Plus plan subscribers. 
                Upgrade your account to connect external services and receive real-time data.
              </Text>
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => window.location.href = '/account'}
              >
                Upgrade to Premium
              </Button>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div>
      <Nav />
      <main className="p-4 md:p-10 mx-auto max-w-7xl">
        <Flex className="flex-col sm:flex-row gap-4 sm:gap-0">
          <div className="flex-1">
            <Title>Integrations</Title>
            <Text>Connect external services to receive real-time data and alerts from your devices.</Text>
          </div>
          <div className="flex-shrink-0">
            <Button 
              variant="primary" 
              size="xs" 
              icon={PlusIcon}
              onClick={handleAddWebhook}
              className="w-full sm:w-auto"
            >
              Add Integration
            </Button>
          </div>
        </Flex>
        
        <Divider />

        {/* Status Messages */}
        {error && (
          <Callout title="Error" color="red" className="mb-4">
            {error}
          </Callout>
        )}
        
        {success && (
          <Callout title="Success" color="green" className="mb-4">
            {success}
          </Callout>
        )}

        {/* Webhooks Table */}
        <Card className="mt-6">
          {webhooks.length > 0 ? (
            <WebhookTable
              webhooks={webhooks}
              onEdit={handleEditWebhook}
              onDelete={handleDeleteWebhook}
              onTest={handleTestWebhook}
            />
          ) : (
            <EmptyIntegrations onAdd={handleAddWebhook} />
          )}
        </Card>

        {/* Webhook Form Modal */}
        {showForm && (
          <WebhookForm
            webhook={editingWebhook}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingWebhook(null);
            }}
          />
        )}
      </main>
    </div>
  );
}